import React, {ChangeEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useUI} from 'coreHooks';

import Button from 'brandComponents/Button';
import Icon from 'brandComponents/Icon';
import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';
import Typography from 'brandComponents/Typography';

import DropdownContext from '../../contexts/DropdownContext';

import {getAttrs} from './helpers';
import {IDropdownMenuProps} from './IDropdownMenu';
import messages from './messages';

import styles from './DropdownMenu.module.scss';

const DropdownMenu = ({
	title,
	searchable,
	closable,
	data,
	onSelect,
	classNames,
	placement,
	headerActionComponent = null,
	container = null,
	behavioralBreakpoints = null
}: IDropdownMenuProps) => {
	const {value, showMenu, handleShowMenu, menuCoords, setMenuRef, menuDirection} = useContext(DropdownContext);
	const [searchQuery, setSearchQuery] = useState('');
	const [filteredData, setFilteredData] = useState([]);
	const [isFirstShow, setIsFirstShow] = useState(true);
	const menuRef = useRef<HTMLDivElement>();
	const menuInnerRefMob = useRef<HTMLDivElement>();
	const menuInnerRefDsk = useRef<HTMLDivElement>();
	const intl = useIntl();
	const isServerUI = useUI('isServer');
	const appEl = useMemo(() => !isServerUI && document.querySelector('#app-main'), [isServerUI]);

	const handleSearch = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
		const newValue = event.target.value;
		setSearchQuery(newValue);
		setFilteredData(data.filter((item) => item.label?.toLowerCase().indexOf(newValue?.toLowerCase()) !== -1));
	}, [data]);

	const handleOnSelect = useCallback(
		(newValue: string) => () => {
			handleShowMenu(false);
			onSelect(newValue);
		},
		[],
	);

	// Reset the filtered data and search query when the menu is opened or data changes
	useEffect(() => {

		// We set the data only on on first show to reduce the page payload size
		if (
			showMenu
			&& isFirstShow
			&& ((data.length && !filteredData.length) || data.length !== filteredData.length)
		) {
			setIsFirstShow(false);
			setFilteredData(data);
		} else if (showMenu && searchQuery) {
			setSearchQuery('');
			setFilteredData(data);
		}
	}, [showMenu]);

	useEffect(() => {
		setSearchQuery('');
		setFilteredData(data);
	}, [data]);

	const handleRefSet = useCallback((el: HTMLDivElement) => {
		if (!el) {
			return;
		}

		menuRef.current = el;

		setMenuRef(el);
	}, []);

	const menuInnerAttrs = (isMobile, menuRef) => getAttrs(menuCoords, placement, menuRef.current, isMobile, showMenu);

	// Go out if we are on the server
	if (!appEl) {
		return null;
	}

	const getDropDown = (isMobile, menuInnerRef) => {
		return (
			<div
				className={cn(styles['dropdown__menu__inner'], 'dropdown__menu__inner', menuDirection)}
				{...menuInnerAttrs(isMobile, menuInnerRef)}
				ref={menuInnerRef}
			>
				{!!title && (
					<Typography variant="h4" classNames={styles['dropdown__title']}>
						{title}
					</Typography>
				)}

				{headerActionComponent}

				{closable && (
					<button
						type="button"
						onClick={() => handleShowMenu(false)}
						className={cn(styles['dropdown__close'], 'dropdown__close')}
					>
						<Icon name="close" />
					</button>
				)}

				{searchable && (
					<input
						type="search"
						name="search"
						className={styles['dropdown__search']}
						placeholder={intl.formatMessage(messages.search)}
						value={searchQuery}
						onChange={handleSearch}
					/>
				)}

				<ul className={cn(styles['dropdown__list'], 'dropdown__list')}>
					{filteredData.map((item) => (

						<li
							key={`${title}_${item?.value}`}
							data-item={item?.label}
							data-value={item?.value}
							className={cn(styles['dropdown__item'], {
								// eslint-disable-next-line
								[styles['dropdown__item--active']]: item?.label?.toString().toLowerCase() === value?.toString().toLowerCase(),
							})}
						>
							<button
								type="button"
								onClick={handleOnSelect(item?.value)}
								className={styles['dropdown__item__btn']}
							>
								{item?.label}
							</button>
						</li>
					))}
					{searchable && !filteredData.length && (
						<li className={styles.dropdown__item}>
							<span className={cn(styles['dropdown__item__btn'], styles['dropdown__item__btn--nothing'])}>
								{intl.formatMessage(messages.nothing)}
							</span>
						</li>
					)}
				</ul>
			</div>
		);
	};

	return createPortal(
		<div
			className={cn(styles['dropdown__menu'], classNames, {
				[styles['dropdown__menu--visible']]: showMenu,
			})}
			ref={handleRefSet}
		>
			<ShowOrHideOnBreakpoint
				query={behavioralBreakpoints?.mobile || '<=small'}
				classNames={styles['dropdown__backdrop-mobile']}
			>
				<Button
					variant="anything"
					classNames={cn(styles['dropdown__backdrop'], 'dropdown__backdrop', {
						[styles['dropdown__backdrop--visible']]: showMenu,
					})}
					ariaLabel={intl.formatMessage(messages.closeButton)}
					onClick={() => handleShowMenu(false)}
				/>
				{getDropDown(true, menuInnerRefMob)}
			</ShowOrHideOnBreakpoint>
			<ShowOrHideOnBreakpoint
				query={behavioralBreakpoints?.desktop || '>small'}
				classNames={styles['dropdown__backdrop-desktop']}
			>
				{getDropDown(false, menuInnerRefDsk)}
			</ShowOrHideOnBreakpoint>
		</div>,
		container || appEl,
	);
};

export default DropdownMenu;
